import React from 'react';
import { TouchableWithoutFeedback, Keyboard, View, ViewStyle, Platform } from 'react-native';

interface KeyboardDismissWrapperProps {
  children: React.ReactNode;
  style?: ViewStyle;
  disabled?: boolean;
}

/**
 * KeyboardDismissWrapper - A reusable component that dismisses the keyboard when tapped
 * 
 * This component solves the common React Native issue where tapping outside of TextInput
 * components doesn't dismiss the keyboard automatically.
 * 
 * On web, this wrapper is disabled by default since web browsers handle focus differently
 * and the TouchableWithoutFeedback can block clicks on input fields.
 * 
 * Usage:
 * <KeyboardDismissWrapper>
 *   <YourContent />
 * </KeyboardDismissWrapper>
 * 
 * Props:
 * - children: The content to wrap
 * - style: Optional style for the wrapper View
 * - disabled: If true, keyboard dismissal is disabled (useful for conditional behavior)
 * 
 * Note: The accessible={false} prop prevents screen readers from treating this as a button
 */
export const KeyboardDismissWrapper: React.FC<KeyboardDismissWrapperProps> = ({
  children,
  style,
  disabled = false,
}) => {
  // On web, disable keyboard dismissal wrapper to prevent blocking input clicks
  if (disabled || Platform.OS === 'web') {
    return <View style={style}>{children}</View>;
  }

  return (
    <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
      <View style={style}>{children}</View>
    </TouchableWithoutFeedback>
  );
};
